import * as React from 'react';
import * as _ from 'lodash';
import { AxiosError } from 'axios';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, MicrositeActionCreators, IMicrositeCampaignResponse } from '@nkr/store/index';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Row, Well } from 'react-bootstrap';
import { FormWrapper } from '../styles';
import { IMicrositeCampaign } from '@nkr/models/microsites';
import { toast } from 'react-toastify';
import CampaignSteps from './campaign-steps';
import LetterGenerator from './letter/letter-generator';

interface IValidation {
    intro: string[],
    closing: string[],
    signature: string[]
}

interface IProps {
    campaignId: number,
    centerId: number,
    isAdmin: boolean,
    previousUrl: string
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getCampaign: (id: number) => Promise<IMicrositeCampaignResponse>,
    updateCampaignLetter: (id: number, salutation: string, intro: string, closing: string, signature: string) => Promise<IMicrositeCampaignResponse>

}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    campaign: IMicrositeCampaign,
    salutation: string,
    intro: string,
    closing: string,
    signature: string,
    // containsSignatureImage: boolean,
    campaignSignatureFileName: string,
    validation: IValidation,
    dirty: boolean,
    allowedCharLimitIntro: number,
    allowedCharLimitClosing: number,
    allowedCharLimitSignature: number,
    mailingList: any[]
}

class EditLetter extends React.Component<TProps, IState> {
    readonly state: IState = {
        campaign: null,
        salutation: '',
        intro: '',
        closing: '',
        signature: '',
        // containsSignatureImage: false,
        campaignSignatureFileName: '',
        validation: {
            intro: [],
            closing: [],
            signature: []
        },
        dirty: false,
        allowedCharLimitIntro: 2000,
        allowedCharLimitClosing: 1000,
        allowedCharLimitSignature: 250,
        mailingList: []
    }

    componentDidMount = () => {
        this.getData();
    }

    validate = () => {
        let { intro, closing, signature } = this.state;

        let validation: IValidation = {
            intro: [],
            closing: [],
            signature: []
        }

        let introMissing = intro.length === 0;

        if (introMissing) {
            validation.intro.push('The body is required.');
        }

        let introTooLong = intro.length > this.state.allowedCharLimitIntro;

        if (introTooLong) {
            validation.intro.push('The body is too long, please limit the body to ' + this.state.allowedCharLimitIntro + ' characters.');
        }

        let closingMissing = closing.length == 0;

        if (closingMissing) {
            validation.closing.push('The left column text is required.');
        }

        let closingTooLong = closing.length > this.state.allowedCharLimitClosing;

        if (closingTooLong) {
            validation.closing.push('The left column text is too long, please limit it to ' + this.state.allowedCharLimitClosing + ' characters.');
        }

        let signatureTooLong = signature.length > this.state.allowedCharLimitSignature;

        if (signatureTooLong) {
            validation.signature.push('The signature is too long, please limit it to ' + this.state.allowedCharLimitSignature + ' characters.');
        }

        return validation;
    }

    getReturnUrl = () => {
        let href = this.props.isAdmin ? `/admin/microsite/campaigns` : `/center/${this.props.centerId}/microsite/campaigns`;
        return href;
    }

    getData = () => {
        this.props.showLoading('Getting the campaign, one moment please...').then(() => {
            this.props.getCampaign(this.props.campaignId)
                .then(response => {
                    if (response.data.success) {
                        let campaign = response.data.results;

                        let salutation = '';
                        let intro = '';
                        let closing = '';
                        let signature = '';

                        if (campaign.letter) {
                            salutation = campaign.letter.salutation;
                            intro = campaign.letter.intro;
                            closing = campaign.letter.closing;
                            signature = campaign.letter.signature;
                        }

                        // if (campaign.campaignSignatureFileName != '') {
                            
                        // }

                        this.setState({
                            campaign,
                            salutation,
                            intro,
                            closing,
                            signature,
                            mailingList: response.data.results.mailingList,
                            campaignSignatureFileName: campaign.campaignSignatureFileName
                        });
                    }
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    isValid = () => {
        let { intro, closing, signature } = this.state.validation;
        return intro.length == 0 && closing.length == 0 && signature.length == 0;
    }

    handleSubmit = (submitToNkr: boolean) => {
        let validation = this.validate();

        this.setState({
            validation,
            dirty: true
        }, () => {
            if (this.isValid()) {
                let { salutation, intro, closing, signature } = this.state;

                this.props.showLoading('Saving letter contents, one moment please...').then(() => {
                    this.props.updateCampaignLetter(this.props.campaignId, salutation, intro, closing, signature).then(response => {
                        this.props.hideLoading();

                        if (response.data.success) {
                            toast.success('Letter contents saved successfully...');
                        } else {
                            toast.error('Unable to save the letter contents...');
                        }

                    }).catch((error: AxiosError) => {
                        this.props.hideLoading();
                        toast.error('Unable to save the letter contents, the following error occurred: ' + error.message);
                    });
                });
            }

        });

    }

    handleFieldChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let newState = _.cloneDeep(this.state);

        let { name, value } = e.currentTarget;

        newState[name] = value;

        this.setState(newState);
    }

    handleCancel = () => {
        let href = this.getReturnUrl();
        window.location.href = href;
    }

    render = () => {
        const isValid = this.isValid();
        const introCharsRemaining = this.state.allowedCharLimitIntro - this.state.intro.length;
        const closingCharsRemaining = this.state.allowedCharLimitClosing - this.state.closing.length;
        const signatureCharsRemaining = this.state.allowedCharLimitSignature - this.state.signature.length;

        return (
            <>
                <Row>
                    <Col xs={12} md={8} mdOffset={2}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
                            <h1 className='text-left' style={{ margin: 0 }}>Edit Letter</h1>
                            <div className='pull-right'>
                                <Button bsStyle='link' onClick={this.handleCancel}>Return to Campaign List</Button>
                            </div>
                        </div>

                        <br />

                        {this.state.dirty && !isValid && (
                            <Alert bsStyle='danger'>
                                <div style={{ marginBottom: 10 }}>The following items prevented us from accepting the letter contents:</div>
                                <ul>
                                    {this.state.validation.intro.map((error, index) =>
                                        <li key={'ie-' + index}>{error}</li>
                                    )}

                                    {this.state.validation.closing.map((error, index) =>
                                        <li key={'ie-' + index}>{error}</li>
                                    )}

                                    {this.state.validation.signature.map((error, index) =>
                                        <li key={'ie-' + index}>{error}</li>
                                    )}
                                </ul>
                            </Alert>
                        )}

                        <Well>
                            <Row>
                                <Col xs={12} sm={3} componentClass={ControlLabel}>Campaign:</Col>
                                <Col xs={12} lg={9}>{this.state.campaign?.campaignName}</Col>
                            </Row>

                            <Row>
                                <Col xs={12} sm={3} componentClass={ControlLabel}>Campaign Type:</Col>
                                <Col xs={12} lg={9}>Mailed Out</Col>
                            </Row>

                            <Row>
                                <Col xs={12} sm={3} componentClass={ControlLabel}>NKR will process the mailing:</Col>
                                <Col xs={12} lg={9}>{this.state.campaign?.processedByNkr ? 'Yes' : 'No'}</Col>
                            </Row>
                        </Well>

                        <FormWrapper style={{ marginBottom: 25 }}>
                            <FormGroup style={{ alignItems: 'flex-start' }}>
                                <Col xs={12} sm={3} componentClass={ControlLabel} style={{ paddingTop: 5 }}>Salutation:</Col>
                                <Col xs={12} lg={9}>
                                    <FormControl
                                        placeholder='Dear [CENTER-ALIAS] Patient,'
                                        value={this.state.salutation}
                                        name='salutation'
                                        onChange={this.handleFieldChange}
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup style={{ alignItems: 'flex-start' }} validationState={this.state.dirty && this.state.validation.intro.length > 0 ? 'error' : null}>
                                <Col xs={12} sm={3} componentClass={ControlLabel} style={{ paddingTop: 5 }}>Body:</Col>
                                <Col xs={12} lg={9}>
                                    <FormControl
                                        componentClass='textarea'
                                        style={{ height: 225, resize: 'none' }}
                                        value={this.state.intro}
                                        name='intro'
                                        onChange={this.handleFieldChange}
                                    />
                                    <div style={{ textAlign: 'right' }}>{introCharsRemaining} character(s) remaining</div>
                                </Col>
                            </FormGroup>

                            <FormGroup style={{ alignItems: 'flex-start' }}>
                                <Col xs={12} sm={3} componentClass={ControlLabel} style={{ paddingTop: 5 }}>Right column image:</Col>
                                <Col xs={12} lg={9}>
                                    <div style={{ border: '1px solid #ccc', borderRadius: 4, padding: 10 }}>
                                        <div className='row'>
                                            <div className='col-xs-12 col-lg-12 text-center'>
                                                <img src='/images/microsite-preview-5.png' />
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </FormGroup>

                            <FormGroup style={{ alignItems: 'flex-start' }} validationState={this.state.dirty && this.state.validation.closing.length > 0 ? 'error' : null}>
                                <Col xs={12} sm={3} componentClass={ControlLabel} style={{ paddingTop: 5 }}>Left Column:</Col>
                                <Col xs={12} lg={9}>
                                    <FormControl
                                        componentClass='textarea'
                                        style={{ height: 150, resize: 'none' }}
                                        value={this.state.closing}
                                        name='closing'
                                        onChange={this.handleFieldChange}
                                    />
                                    <div style={{ textAlign: 'right' }}>{closingCharsRemaining} character(s) remaining</div>
                                </Col>
                            </FormGroup>

                            <FormGroup style={{ alignItems: 'flex-start' }} validationState={this.state.dirty && this.state.validation.signature.length > 0 ? 'error' : null}>
                                <Col xs={12} sm={3} componentClass={ControlLabel} style={{ paddingTop: 5 }}>Signature:</Col>
                                <Col xs={12} lg={9}>
                                    <FormControl
                                        componentClass='textarea'
                                        style={{ height: 70, resize: 'none' }}
                                        value={this.state.signature}
                                        name={'signature'}
                                        onChange={this.handleFieldChange}
                                    />
                                    <div style={{ textAlign: 'right' }}>{signatureCharsRemaining} character(s) remaining</div>
                                </Col>
                            </FormGroup>

                            <FormGroup style={{ alignItems: 'flex-start' }}>
                                <Col xs={12} sm={3} componentClass={ControlLabel} style={{ paddingTop: 5 }}>Signature Image:</Col>
                                <Col xs={12} lg={9}>
                                    <div style={{ border: '1px solid #ccc', borderRadius: 4, padding: 10 }}>
                                        <div className='row'>
                                            <div className='col-xs-12 col-lg-12 text-center'>
                                                {this.state.campaignSignatureFileName != '' ?
                                                    <img src={__API__ + '/microsite/campaign/' + this.props.campaignId + '/signature-file'} style={{ width: 200, height: 100, objectFit: 'contain' }} />
                                                    :
                                                    <div>[Signature image not uploaded]</div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </FormGroup>
                        </FormWrapper>

                        <Well bsSize='sm'>
                            <CampaignSteps currentStep={5} campaignId={this.props.campaignId} />
                        </Well>

                        <FormGroup>
                            <div className='col-xs-12 col-lg-12 text-center'>
                                <Button bsStyle='primary' style={{ marginRight: 10 }} onClick={() => this.handleSubmit(false)}>Save</Button>
                                <LetterGenerator campaign={this.state.campaign} data={this.state.mailingList} />
                                <Button bsStyle='link' onClick={this.handleCancel}>Return to the Campaign List</Button>
                            </div>
                        </FormGroup>

                    </Col>
                </Row>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getCampaign: (id) => {
            return dispatch(MicrositeActionCreators.getCampaign(id, true));
        },
        updateCampaignLetter: (id, salutation, intro, closing, signature) => {
            return dispatch(MicrositeActionCreators.updateCampaignLetter(id, salutation, intro, closing, signature));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(EditLetter);